<template>
  <div
    v-if="options?.length"
    ref="wrapper"
    class="checkbox-container"
  >
    <div
      v-for="option, index in options"
      :key="option.key || option.value"
      class="checkbox-wrapper"
      :class="{[spacing]: index !== options.length - 1}"
    >
      <a
        :href="option?.url"
        role="checkbox"
        :aria-checked="modelValue.includes(option.value)"
        :aria-label="option.ariaLabel || option.label"
        :aria-describedby="'checkbox'+id"
        tabindex="0"
        :class="{
          'is-invalid-input': hasError,
        }"
        :disabled="option.disabled"
        @click.prevent="onClick(option)"
        @blur="handleChange"
      >
        <span
          v-if="showCheckbox"
          class="checkbox"
        />
        <template v-if="option.labelType?.toLowerCase() === 'html'">
          <component
            :is="option.label"
            v-if="option.label"
          />
        </template>
        <template v-else>{{ option.label }}</template>
      </a>
      <component
        :is="option.additionalText"
        v-if="option.additionalText"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import generateRandomId from '@atomic-ui/../utils/randomNumber';

const wrapper = ref(null);
const props = defineProps({
  name: {
    type: String,
    default: 'checkbox',
  },
  options: {
    type: Array,
    required: true,
    validator(value) {
      return Array.isArray(value) && value.length > 0;
    },
  },
  spacing: {
    type: String,
    default: 'margin-bottom-xxs',
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  showCheckbox: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['update:modelValue']);
const id = generateRandomId();
const inputValue = ref(props.modelValue);
const onClick = ({ value }) => {
  if (inputValue.value.includes(value)) {
    inputValue.value.splice(inputValue.value.indexOf(value), 1);
  } else {
    inputValue.value.push(value);
  }
  emit('update:modelValue', inputValue.value);
};
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});
const handleChange = (event) => {
  // inputValue.value should be empty, returning reference
  const isEmptyValue = event.value === undefined && inputValue.value.length === 0;

  // emit only when wrapper blur
  const isWrapperBlur = wrapper.value && !wrapper.value.contains(event.relatedTarget);
  if (isEmptyValue && isWrapperBlur) {
    emit('update:modelValue', inputValue.value);
  }
};
</script>
<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;

.checkbox-wrapper {
  align-items: flex-start;
  display: flex;
}

a{
  text-decoration: none;
  display: flex;
  align-items: center;
  cursor: default;
}

.checkbox{
    position: relative;
    display: inline-block;
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid #000;
    border-radius: 0;
    padding: 0 0.6875rem;
    cursor: pointer;
    color: #fff;
    margin-right: 0.5rem;
}

a[aria-checked=true]{
  .checkbox::after{
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
  }
}

</style>

<style lang="scss" brand="mcom">
@import '@core/csg/src/scss/encodedIcons/mcom/_confirmation-black-checkmark.scss';

a[aria-checked=true]{
  .checkbox::after{
    content: $confirmation-black-checkmark;
  }
}

</style>
<style lang="scss" brand="bcom">
@import '@core/csg/src/scss/encodedIcons/bcom/_confirmation-checkmark.scss';

a[aria-checked=true]{
  .checkbox::after{
    content: $confirmation-checkmark;
  }
}
</style>
